import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from "react";
import { Box, IconButton, InputAdornment, TextField, Typography, type TextFieldProps } from "@mui/material";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { Calendar } from "lucide-react";

import { DISPLAY_DATE_FORMAT, displayDateToIso, isoDateToDisplay, normalizeDisplayDateSeparators } from "../../lib/api/b2bDates";
import IsoDatePickerPopover from "./IsoDatePickerPopover";
import { periodFieldSx, type PeriodTone } from "./periodFieldColors";

dayjs.extend(isoWeek);

const WEEKDAY_SHORT = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"] as const;

type IsoDateFieldProps = Omit<TextFieldProps, "type" | "value" | "onChange"> & {
    value: string;
    onChange: (isoValue: string) => void;
    /** 1 = teal, 2 = оранжев, 3 = лилав. */
    tone?: PeriodTone;
    /** Показва ден от седмицата до полето (Пн…Нд). */
    showWeekday?: boolean;
};

/** Компактно поле за дата + тъмен календар (единен в CRM). */
export default function IsoDateField({
    value,
    onChange,
    label,
    sx,
    fullWidth,
    tone,
    showWeekday = false,
    ...rest
}: IsoDateFieldProps) {
    const fieldRef = useRef<HTMLDivElement>(null);
    const [draft, setDraft] = useState(() => isoDateToDisplay(value));
    const [pickerOpen, setPickerOpen] = useState(false);

    useEffect(() => {
        setDraft(isoDateToDisplay(value));
    }, [value]);

    const weekdayLabel = useMemo(() => {
        if (!showWeekday || !value || !dayjs(value).isValid()) {
            return "";
        }
        return WEEKDAY_SHORT[dayjs(value).isoWeekday() - 1] ?? "";
    }, [showWeekday, value]);

    function commitDraft() {
        const trimmed = draft.trim();

        if (!trimmed) {
            if (value !== "") {
                onChange("");
            }
            setDraft("");
            return;
        }

        const iso = displayDateToIso(trimmed);

        if (iso) {
            onChange(iso);
            setDraft(isoDateToDisplay(iso));
            return;
        }

        setDraft(isoDateToDisplay(value));
    }

    function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
        if (event.key !== "Enter") {
            return;
        }

        event.preventDefault();
        commitDraft();
        event.currentTarget.blur();
    }

    function openPicker(event: MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        event.stopPropagation();
        setPickerOpen(true);
    }

    return (
        <Box
            ref={fieldRef}
            sx={[
                {
                    position: "relative",
                    width: fullWidth ? "100%" : 156,
                    flex: fullWidth ? "1 1 auto" : "0 0 auto",
                    minWidth: fullWidth ? 0 : 148,
                },
                ...(tone != null ? [periodFieldSx(tone)] : []),
                ...(Array.isArray(sx) ? sx : sx != null ? [sx] : []),
            ]}
        >
            <TextField
                {...rest}
                fullWidth
                label={label}
                size="small"
                value={draft}
                placeholder={DISPLAY_DATE_FORMAT}
                onChange={(event) => setDraft(normalizeDisplayDateSeparators(event.target.value))}
                onBlur={commitDraft}
                onKeyDown={handleKeyDown}
                slotProps={{
                    ...rest.slotProps,
                    input: {
                        ...rest.slotProps?.input,
                        sx: { pr: 0.25 },
                        endAdornment: (
                            <InputAdornment position="end" sx={{ gap: 0.25 }}>
                                {weekdayLabel ? (
                                    <Typography
                                        component="span"
                                        variant="caption"
                                        sx={{
                                            fontWeight: 700,
                                            color: "text.secondary",
                                            minWidth: 22,
                                            textAlign: "center",
                                            userSelect: "none",
                                        }}
                                    >
                                        {weekdayLabel}
                                    </Typography>
                                ) : null}
                                <IconButton
                                    size="small"
                                    edge="end"
                                    aria-label={`Избери ${label ?? "дата"} от календар`}
                                    onClick={openPicker}
                                    sx={{ color: "text.secondary", p: 0.5 }}
                                >
                                    <Calendar size={16} strokeWidth={2} />
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                    inputLabel: { shrink: true, ...rest.slotProps?.inputLabel },
                    htmlInput: {
                        ...rest.slotProps?.htmlInput,
                        sx: { fontVariantNumeric: "tabular-nums", letterSpacing: 0.15 },
                    },
                }}
            />
            <IsoDatePickerPopover
                open={pickerOpen}
                anchorEl={fieldRef.current}
                value={value}
                onClose={() => setPickerOpen(false)}
                onChange={(isoValue) => {
                    onChange(isoValue);
                    setDraft(isoDateToDisplay(isoValue));
                }}
            />
        </Box>
    );
}
